import React, { CSSProperties, useMemo, useRef } from 'react';

import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Box, Flex } from '@chakra-ui/react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { usePartialPublishEventDetails } from 'screens/performanceReview/helpers';
import { addPluralSuffixS } from 'utils/helpers/stringHelpers';

import { RightArrowCircle } from 'assets/assetsV1/iconComponents/RightArrowCircle';

import { KH_LINKS } from 'constants/knowledgeHubLinks';
import {
  sendPPCancelFirstScreenEvent,
  sendPPNextFirstScreenEvent,
  sendPPNoActionExpansionEvent,
  sendPPSearchInNoActionEvent,
  sendPPShareResultsKBaseEvent,
  sendPPSwitchPublishOptionsEvent,
} from 'constants/mixpanelHelpers/partialPublishEvents';

import { AgGridWrapper } from 'uiToolkitV1/AgGridWrapper';
import { Avatar } from 'uiToolkitV1/Avatar';
import { DrawerBody, DrawerFooter, DrawerHeaderSecondary } from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { Divider } from 'uiToolkitV1/Divider';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { Radio } from 'uiToolkitV1/Radio';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TagColored } from 'uiToolkitV1/Tag';
import { TagOutlined } from 'uiToolkitV1/Tag/TagOutlined';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { useBulkActionContext } from '../BulkActions/BulkActionContext';
import { SearchBar } from '../SearchBar';
import {
  PARTIAL_PUBLISH_SCREENS,
  REVIEW_STATUS_COLORS,
  REVIEW_STATUS_LABELS,
  usePartialPublishContext,
  Screen,
} from './PartialPublishContext';
import { EmployeeWithReviewStatus, Section, useGetPartialPublishOptionsQuery } from './dataHooks';

const AvatarComponent = (data: ICellRendererParams<EmployeeWithReviewStatus>['data']) => () => (
  <Avatar name={data.employeeDetails.displayName} src={data.employeeDetails.profilePhoto} />
);

const EmployeeRenderer: React.FC<ICellRendererParams<EmployeeWithReviewStatus>> = ({ data }) => (
  <ListItemComp
    LeftComponent={AvatarComponent(data)}
    title={data.employeeDetails.displayName}
    withoutBackground
    isBoldText={false}
  />
);

const StatusRenderer: React.FC<ICellRendererParams<EmployeeWithReviewStatus>> = ({ data }) => (
  <TagColored color={REVIEW_STATUS_COLORS[data.reviewStatus]}>{REVIEW_STATUS_LABELS[data.reviewStatus]}</TagColored>
);

const PublishOptions = ({ onClose }) => {
  const { selectedSection, setScreen, setSelectedSection } = usePartialPublishContext();
  const { selectedEmployeeIds } = useBulkActionContext();

  const { data, isLoading } = useGetPartialPublishOptionsQuery();

  const managerSection = data?.sections.find((s) => s.section === 'ALLOW_MANAGER_PUBLISH');
  const employeeSection = data?.sections.find((s) => s.section === 'ALLOW_EMPLOYEE_PUBLISH');
  const noActionSection = data?.sections.find((s) => s.section === 'NO_ACTION');

  if (!selectedSection && (managerSection || employeeSection)) {
    setSelectedSection(managerSection ?? employeeSection);
  }

  const columnDefs = useMemo<ColDef<EmployeeWithReviewStatus>[]>(
    () => [
      {
        cellRenderer: EmployeeRenderer,
        valueGetter: (props) => props.data.employeeDetails.displayName,
      },
      {
        cellRenderer: StatusRenderer,
        valueGetter: (props) => props.data.reviewStatus,
      },
    ],
    []
  );

  const gridStyle = useMemo<CSSProperties>(
    () => ({
      height: '280px',
      width: '100%',
      marginTop: '8px',
    }),
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      flex: 1,
      cellStyle: { display: 'flex', alignItems: 'center', cursor: 'default' },
    }),
    []
  );

  const gridRef = useRef<AgGridReact>();

  const gridStatePersistenceKey = null;

  const { module, empId, page } = usePartialPublishEventDetails();

  const sendExpansionEvent = () => {
    sendPPNoActionExpansionEvent({
      eventProperties: {
        page,
        source: module,
        empId,
      },
    });
  };

  const debounceTimer = useRef(null);

  const sendSearchEvent = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      sendPPSearchInNoActionEvent({
        eventProperties: {
          page,
          source: module,
          empId,
          searchNoAction: true,
        },
      });
    }, 2000);
  };

  const sendKBaseEvent = () => {
    sendPPShareResultsKBaseEvent({
      eventProperties: {
        page,
        source: module,
        empId,
        shareKb: true,
      },
    });
  };

  const setSelectedSectionWithEvent = (section: Section) => {
    if (section === selectedSection) return;
    sendPPSwitchPublishOptionsEvent({
      eventProperties: {
        page,
        source: module,
        empId,
      },
    });
    setSelectedSection(section);
  };

  const onCloseWithEvent = () => {
    sendPPCancelFirstScreenEvent({
      eventProperties: {
        page,
        source: module,
        empId,
      },
    });
    onClose();
  };

  const setScreenWithEvent = (screen: Screen) => {
    sendPPNextFirstScreenEvent({
      eventProperties: {
        page,
        source: module,
        empId,
      },
    });
    setScreen(screen);
  };

  return (
    <>
      <DrawerHeaderSecondary onClose={onClose}>
        <Flex
          alignItems="center"
          borderBottom="1px"
          borderBottomColor="neutral.CFD7DF"
          mx={4}
          py={5}
          justifyContent="space-between"
        >
          <Flex flexDir="column">
            <TextBodyMedium bold>Share Results</TextBodyMedium>
            <Flex gap={1} alignItems="center">
              <a
                href={KH_LINKS.PARTIAL_PUBLISH_SHARE_RESULTS.url}
                target="_blank"
                rel="noreferrer"
                onClick={sendKBaseEvent}
              >
                <TextBodyMedium color="functional.1565BC">
                  {KH_LINKS.PARTIAL_PUBLISH_SHARE_RESULTS.description}
                </TextBodyMedium>
              </a>
            </Flex>
          </Flex>
          <TagOutlined StartIcon={null} startAvatarSrc={null} onClick={() => {}}>
            <TextBodyMedium bold>
              {selectedEmployeeIds.length} Employee{addPluralSuffixS(selectedEmployeeIds.length)}
            </TextBodyMedium>
          </TagOutlined>
        </Flex>
      </DrawerHeaderSecondary>
      <DrawerBody>
        <Flex flexDir="column" mx={5} py={5} h="100%">
          {isLoading && <SkeletonLoader />}
          {managerSection && (
            <>
              <Flex
                cursor="pointer"
                alignItems="flex-start"
                onClick={() => setSelectedSectionWithEvent(managerSection)}
              >
                <Radio
                  mt={2}
                  isChecked={selectedSection?.section === 'ALLOW_MANAGER_PUBLISH'}
                  name=""
                  onChange={() => {}}
                />
                <Flex flexDir="column" ml={3}>
                  <TextBodyMedium bold>Enable managers to share results to employees</TextBodyMedium>
                  <TextLabel>Employees whose publishing of reviews to managers is not complete</TextLabel>
                  <Box mt={3}>
                    <TagOutlined StartIcon={null} startAvatarSrc={null} onClick={() => {}}>
                      <TextLabel bold>
                        {managerSection?.employeeStatusList.length} Employee
                        {addPluralSuffixS(managerSection?.employeeStatusList.length)}
                      </TextLabel>
                    </TagOutlined>
                  </Box>
                </Flex>
              </Flex>
              <Divider color="neutral.EBEFF3" my={6} />
            </>
          )}
          {employeeSection && (
            <>
              <Flex
                cursor="pointer"
                alignItems="flex-start"
                onClick={() => setSelectedSectionWithEvent(employeeSection)}
              >
                <Radio
                  mt={2}
                  isChecked={selectedSection?.section === 'ALLOW_EMPLOYEE_PUBLISH'}
                  name=""
                  onChange={() => {}}
                />
                <Flex flexDir="column" ml={3}>
                  <TextBodyMedium bold>Share results to employees</TextBodyMedium>
                  <TextLabel>Employees whose publishing of reviews is not complete</TextLabel>
                  <Box mt={3}>
                    <TagOutlined StartIcon={null} startAvatarSrc={null} onClick={() => {}}>
                      <TextLabel bold>
                        {employeeSection?.employeeStatusList.length} Employee
                        {addPluralSuffixS(employeeSection?.employeeStatusList.length)}
                      </TextLabel>
                    </TagOutlined>
                  </Box>
                </Flex>
              </Flex>
              <Divider color="neutral.EBEFF3" my={6} />
            </>
          )}
          {noActionSection && (
            <Accordion allowToggle>
              <AccordionItem border={0}>
                <AccordionButton
                  p={0}
                  _hover={{ bgColor: 'white' }}
                  _expanded={{
                    svg: {
                      transform: 'rotate(90deg)',
                    },
                  }}
                  onClick={sendExpansionEvent}
                >
                  <Flex cursor="pointer" alignItems="flex-start">
                    <RightArrowCircle fill="transparent" color="primary.1565BC" mt={2} />
                    <Flex flexDir="column" ml={3} alignItems="flex-start">
                      <TextBodyMedium bold>No action required</TextBodyMedium>
                      <TextLabel>Employees whose forms are already published or not complete yet</TextLabel>
                      <Box mt={3}>
                        <TagOutlined StartIcon={null} startAvatarSrc={null} onClick={() => {}}>
                          <TextLabel bold>
                            {noActionSection?.employeeStatusList.length} Employee
                            {addPluralSuffixS(noActionSection?.employeeStatusList.length)}
                          </TextLabel>
                        </TagOutlined>
                      </Box>
                    </Flex>
                  </Flex>
                </AccordionButton>
                <AccordionPanel pt={5} pl={7} pr={1}>
                  <Box p={3} borderRadius="8px" borderWidth="1px" borderColor="neutral.EBEFF3">
                    <SearchBar
                      gridRef={gridRef}
                      placeholder="Search Employees"
                      width="100%"
                      onChange={sendSearchEvent}
                    />
                    <div style={gridStyle} className="ag-theme-alpine ag-theme-partial-publish">
                      <AgGridWrapper
                        gridStatePersistenceKey={gridStatePersistenceKey}
                        useColumnStatePersistence={false}
                        useFilterModelPersistence={false}
                        ref={gridRef}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        rowData={noActionSection.employeeStatusList}
                        headerHeight={0}
                        rowHeight={56}
                      />
                    </div>
                  </Box>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          )}
          {selectedSection?.competencyScorePushEmpCount ? (
            <Box borderRadius="8px" mt="auto" p="12px 16px" bgColor="neutral.FDFDFD" border="1px solid #EBEFF3">
              <TextBodyMedium>
                Note : This review result includes manager ratings on competencies. Publishing this result will also
                update the overall competency level for{' '}
                <span style={{ fontWeight: 600 }}>{selectedSection.competencyScorePushEmpCount}</span> employee
                {addPluralSuffixS(selectedSection.competencyScorePushEmpCount)}.
              </TextBodyMedium>
            </Box>
          ) : null}
        </Flex>
      </DrawerBody>
      <DrawerFooter mt="auto">
        <Button w="94px" variant="outlined" mr={4} onClick={onCloseWithEvent}>
          Cancel
        </Button>
        <Button
          w="94px"
          variant="filled"
          colorScheme="blue"
          onClick={() => setScreenWithEvent(PARTIAL_PUBLISH_SCREENS.EMPLOYEES_LIST)}
          disabled={!selectedSection}
        >
          Next
        </Button>
      </DrawerFooter>
    </>
  );
};

export default PublishOptions;
